import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, CheckCircle2, Clock, History, ShieldCheck, XCircle } from 'lucide-react'
import { adminApi, type BookingAttendeeCertificateDto, type BookingAttendeeDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useIssueProgress, IssueProgressBar } from './IssueProgress'

const CYCLE = ['NotRecorded', 'Present', 'Absent']
const LABEL: Record<string, string> = { NotRecorded: 'Not recorded', Present: 'Present', Absent: 'Absent' }
const STYLE: Record<string, string> = {
  Present: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200',
  Absent: 'bg-red-50 text-red-600 border-red-300 hover:bg-red-100',
  NotRecorded: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
}
const ICON: Record<string, React.ReactNode> = {
  Present: <CheckCircle2 size={13} />, Absent: <XCircle size={13} />, NotRecorded: <Clock size={13} />,
}

const qualifies = (status: string, outcome: string | null) => status === 'Present' && outcome !== 'Failed'

const errorText = (err: unknown) => {
  const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

/**
 * The attendance list of a COMPLETED booking. A completed booking is final, so mistakes are put right here:
 * attendance, pass/fail and ID checks can all be corrected, each correction is recorded in the booking's history, and
 * anything that means a person should not have been certified says so (and deletes their record) before it happens.
 */
export function CompletedAttendance({
  bookingId, attendees, certificates, certificatesIssued, thirdParty = false,
}: {
  bookingId: string
  attendees: BookingAttendeeDto[]
  certificates: BookingAttendeeCertificateDto[]
  certificatesIssued: boolean
  thirdParty?: boolean
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const refresh = () => {
    for (const key of ['booking', 'booking-attendee-certificates', 'booking-activity', 'booking-pending-third-party-certificates']) {
      queryClient.invalidateQueries({ queryKey: [key, bookingId] })
    }
    queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
  }

  const attendanceMutation = useMutation({
    mutationFn: (v: { attendeeId: string; status: string; outcome: string | null; confirm: boolean }) =>
      adminApi.updateBookingAttendance(bookingId, v.attendeeId, v.status, v.outcome, v.confirm),
    onSuccess: () => { setError(null); refresh() },
    onError: e => setError(errorText(e)),
  })

  const idMutation = useMutation({
    mutationFn: (v: { attendeeId: string; checked: boolean }) => adminApi.setBookingIdentityCheck(bookingId, v.attendeeId, v.checked),
    onSuccess: () => { setError(null); refresh() },
    onError: e => setError(errorText(e)),
  })

  const hasRecord = (staffMemberId: string) =>
    certificates.find(c => c.staffMemberId === staffMemberId)?.trainingRecordId != null

  /** Change attendance/outcome, first confirming if it would delete this person's training record and certificate. */
  const correct = (a: BookingAttendeeDto, status: string, outcome: string | null) => {
    const removes = certificatesIssued && hasRecord(a.staffMemberId) && !qualifies(status, outcome)
    if (removes) {
      const what = status === 'Present' ? 'Present (failed)' : LABEL[status]
      const ok = confirm(
        `Changing ${a.firstName} ${a.lastName} to "${what}" means they shouldn't have been certified, so their training record and certificate will be deleted.\n\n` +
        'This is recorded in the booking history and can\'t be undone (you can issue them a certificate again later if you mark them present). Continue?')
      if (!ok) return
    }
    attendanceMutation.mutate({ attendeeId: a.attendeeId, status, outcome, confirm: removes })
  }

  return (
    <div className="space-y-1.5">
      {attendees.map(a => {
        const next = CYCLE[(CYCLE.indexOf(a.attendanceStatus) + 1) % CYCLE.length]
        const busy = attendanceMutation.isPending || idMutation.isPending
        const outcomeStyle = a.outcome === 'Failed'
          ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
          : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
        return (
          <div key={a.attendeeId} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-gray-800">{a.firstName} {a.lastName}</span>
                {a.isWalkIn && <span className="text-xs text-gray-400">Walk-in</span>}
              </div>
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer mt-0.5">
                <input
                  type="checkbox"
                  checked={!!a.identityCheckedAt}
                  disabled={busy}
                  onChange={e => idMutation.mutate({ attendeeId: a.attendeeId, checked: e.target.checked })}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                {a.identityCheckedAt
                  ? <><ShieldCheck size={12} className="text-green-600" /> ID checked{a.identityCheckedBy ? ` by ${a.identityCheckedBy}` : ''}{a.identityCheckedByAdmin ? ' (recorded by an administrator)' : ''}</>
                  : 'ID checked'}
              </label>
            </div>

            <div className="flex items-center gap-2">
              {a.attendanceStatus === 'Present' && (
                <button
                  type="button"
                  disabled={busy}
                  title="Click to toggle pass/fail"
                  onClick={() => correct(a, 'Present', a.outcome === 'Failed' ? 'Passed' : 'Failed')}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${outcomeStyle}`}
                >
                  {a.outcome === 'Failed' ? <Ban size={13} /> : <CheckCircle2 size={13} />}
                  {a.outcome === 'Failed' ? 'Failed' : 'Passed'}
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => correct(a, next, next === 'Present' ? (a.outcome ?? 'Passed') : null)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${STYLE[a.attendanceStatus] ?? STYLE.NotRecorded}`}
              >
                {ICON[a.attendanceStatus]}
                {LABEL[a.attendanceStatus] ?? a.attendanceStatus}
              </button>
            </div>
          </div>
        )
      })}

      <MissingCertificatesBanner
        bookingId={bookingId}
        attendees={attendees}
        certificates={certificates}
        certificatesIssued={certificatesIssued}
        thirdParty={thirdParty}
      />

      {error && <p className="text-xs text-red-600 pt-1">{error}</p>}
      <p className="text-xs text-gray-400 pt-1">
        This booking is completed, so it can't be reopened or cancelled. Correct mistakes here: every change is recorded in the booking history below.
      </p>
    </div>
  )
}

/** Corrections made to a completed booking, newest first. */
export function BookingHistoryCard({ bookingId }: { bookingId: string }) {
  const { data = [] } = useQuery({
    queryKey: ['booking-activity', bookingId],
    queryFn: () => adminApi.getBookingActivity(bookingId).then(r => r.data),
  })
  if (data.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><History size={16} className="text-gray-400" /> History</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.map((h, i) => (
          <div key={i} className="text-sm">
            <p className="text-gray-800">{h.text}</p>
            <p className="text-xs text-gray-400">
              {new Date(h.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              {h.by ? ` · ${h.by}` : ''}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/**
 * After certificates have been issued, anyone who is now marked present (and not failed) but has no training record
 * gets one here, without touching anyone else's. Shown on both the attendance list and the Certificates tab.
 */
export function MissingCertificatesBanner({
  bookingId, attendees, certificates, certificatesIssued, thirdParty = false,
}: {
  bookingId: string
  attendees: BookingAttendeeDto[]
  certificates: BookingAttendeeCertificateDto[]
  certificatesIssued: boolean
  /** The awarding body issues the certificate, so CertSlate only creates the training record. */
  thirdParty?: boolean
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const issue = useMutation({
    mutationFn: () => adminApi.issueCertificates(bookingId, true),
    onSuccess: () => {
      setError(null)
      for (const key of ['booking', 'booking-attendee-certificates', 'booking-activity', 'booking-pending-third-party-certificates']) {
        queryClient.invalidateQueries({ queryKey: [key, bookingId] })
      }
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
    },
    onError: e => setError(errorText(e)),
  })

  const progress = useIssueProgress(bookingId, issue.isPending)

  const waiting = attendees.filter(a =>
    certificatesIssued && qualifies(a.attendanceStatus, a.outcome)
    && certificates.find(c => c.staffMemberId === a.staffMemberId)?.trainingRecordId == null)

  if (waiting.length === 0) return null

  const noun = thirdParty
    ? (waiting.length === 1 ? 'training record' : 'training records')
    : (waiting.length === 1 ? 'certificate' : 'certificates')

  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          {waiting.map(a => `${a.firstName} ${a.lastName}`).join(', ')} {waiting.length === 1 ? 'is' : 'are'} marked present but
          {waiting.length === 1 ? ' has' : ' have'} no {thirdParty ? 'training record' : 'certificate'} yet.
        </span>
        <Button size="sm" disabled={issue.isPending} onClick={() => issue.mutate()}>
          {issue.isPending ? 'Issuing…' : `${thirdParty ? 'Create' : 'Issue'} ${noun}`}
        </Button>
      </div>
      {issue.isPending && <IssueProgressBar progress={progress} idleLabel={thirdParty ? 'Creating training records…' : 'Issuing certificates…'} />}
      {error && <p className="text-red-600">{error}</p>}
    </div>
  )
}
